import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ErrorBar,
  LineChart,
  Line,
  Legend,
} from "recharts";
import { roasData } from '../../data/roas.js';

// // Custom label function for ReferenceLine
// const renderCustomLabel = ({ x, y, value }) => (
//   <text x={x} y={y} dy={-10} fill="red" textAnchor="middle" fontSize={14}>
//     {value}
//   </text>
// );



const BarformatTime = (value) => {
  const hours = Math.floor(value);
  const minutes = Math.round((value - hours) * 60);
  return hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`;
};

const CustomTooltip = ({ payload, label }) => {
  if (payload.length === 0) return null;

  const data = payload[0].payload;

  return (
    <div style={{
      backgroundColor: 'white',
      border: '1px solid #ccc',
      borderRadius: '4px',
      padding: '10px',
      boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
    }}>
      <div style={{ fontWeight: 'bold' }}>{label}</div>
      <div>Short: {BarformatTime(data.low)}</div>
      <div>Average: {BarformatTime(data.mid)}</div>
      <div>Long: {BarformatTime(data.high)}</div>
      {/* <div>Error: {BarformatTime(data.errorrelative)}</div> */}
    </div>
  );
};

const convertHoursToTime = (hours) => {
  const totalMinutes = Math.round(hours * 60);
  const hoursPart = Math.floor(totalMinutes / 60);
  const minutesPart = totalMinutes % 60;

  return `${hoursPart}h ${minutesPart}m`;
};

// Custom tooltip component for line chart
const LinearTooltip = ({ payload, label, active }) => {
  if (active && payload && payload.length) {
    const xValue = payload[0].payload.time; 
    const formattedXValue = convertHoursToTime(xValue);
    const lineName = payload[0].name || 'Unknown Line';

    return (
      <div className="custom-tooltip" style={{ backgroundColor: '#fff', border: '1px solid #ccc', padding: '10px' }}>
        <div style={{ fontWeight: 'bold' }}>{lineName}</div>
        <div>Duration: {formattedXValue}</div>
      </div>
    );
  }

  return null;
};

export default function SubstanceInfoDosageRoa({ roa, visible }) {
  const [unit, setUnit] = useState('minutes');
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedPhase, setSelectedPhase] = useState('');

    // Definiujemy funkcję obsługującą kliknięcie na ikonę
    const handleInfoClick = () => {
      alert('Effects start with onset, which is when effects become noticeable. Linear chart shows effects predictions within margin of error. Data comes from tripsit.me and psychonautwiki.org.'); // Tutaj możesz dodać swoją logikę
    };
  

  useEffect(() => {
    if (roa) {
      setSelectedPhase('');
      setSelectedTime('');
    }
  }, [roa]);

  useEffect(() => {
    if (roa && roa.duration) {
      const firstDuration = roa.duration[0].value;
      if (firstDuration.includes('hour')) setUnit('hours');
      else if (firstDuration.includes('minute')) setUnit('minutes');
      else if (firstDuration.includes('second')) setUnit('seconds');
      else setUnit(''); // Default or unknown unit
    }
  }, [roa]);
  
  const abbreviateTimeUnits = (value) => 
    value.replace(/minutes/g, 'm').replace(/hours/g, 'h').replace(/seconds/g, 's');
  
  const convertToHours = (value) => {
    if (!value) return { low: 0, high: 0, mid: 0 };
  
    // Sprawdź, czy `value` jest typu string, zanim użyjesz `split`
    if (typeof value !== 'string') return { low: 0, high: 0, mid: 0 };
  
    // Split the value to get low and high range
    let [low, high] = value.split("-").map((v) => v.trim());
  
    // Upewnij się, że `low` i `high` są zdefiniowane, zanim wywołasz `match`
    const lowValue = low ? low.match(/[\d.]+/g) : null;
    const lowUnit = low ? low.match(/[a-zA-Z]+/g) : null;
    const highValue = high ? high.match(/[\d.]+/g) : null;
    const highUnit = high ? high.match(/[a-zA-Z]+/g) : null;
  
    // Convert to hours using appropriate unit
    const lowConverted = convertValueToHours(lowValue ? parseFloat(lowValue[0]) : 0, lowUnit ? lowUnit[0] : unit);
    const highConverted = convertValueToHours(highValue ? parseFloat(highValue[0]) : 0, highUnit ? highUnit[0] : unit);
  
    return {
      low: lowConverted,
      high: highConverted,
      mid: (lowConverted + highConverted) / 2,
    };
  };
  
  
  const convertValueToHours = (value, unit) => {
    if (isNaN(value)) return 0;
    switch (unit) {
      case 'hours':
      case 'h':
        return value;
      case 'minutes':
      case 'm':
        return value / 60;
      case 'seconds':
      case 's':
        return value / 3600;
      default:
        return value; // Default to hours if unit is unknown
    }
  };

  function timePicked(selectedTime, time) {
    // Konwertuj selectedTime na liczbę minut, domyślnie ustawiając 0, jeśli jest nieprawidłowe
    const selectedTimeValue = parseFloat(selectedTime) || 0;
  
    // Przekształć godziny i minuty na milisekundy
    const selectedTimeInMillis = selectedTimeValue * 60000; // 1 godzina = 60 minut, 1 minuta = 60 000 milisekund
  
    // Pobierz aktualny czas w milisekundach
    const currentTimeInMillis = new Date().getTime();
  
    // Oblicz skorygowany czas
    const adjustedTimeInMillis = currentTimeInMillis + (time * 60 * 60 * 1000 + selectedTimeInMillis);
  
    // Zwróć nową datę
    return new Date(adjustedTimeInMillis);
  }
  
  const calculateRemainingTime = () => {
    const [showInfo, setShowInfo] = useState(false); // Dodaj stan do zarządzania widocznością dodatkowego tekstu
  
    // Return message if no phase or time is selected
    if (!selectedPhase) {
      return 'Select a phase to see the prognosis.';
    }
  
    // Ensure selectedTime is a string and handle cases where it's not
    const selectedTimeString = typeof selectedTime === 'string' ? selectedTime : '';
    const [selectedHour, selectedMinute] = selectedTimeString.split(':').map(Number);
    const selectedTimeInMillis = (selectedHour || 0) * 60 * 60 * 1000 + (selectedMinute || 0) * 60 * 1000;
  
    // Calculate "now" using timePicked function
    const now = new Date();
    const currentTime = timePicked(selectedTime, selectedHour || 0);
  
    // Convert selectedTime to hours if necessary
    const timeInHours = parseFloat(selectedTimeString) / 60 || 0;
  
    // Filter out unwanted phases
    const filteredDurations = roa.duration.filter(
      phase => !/after effects|duration|total/i.test(phase.name)
    );
  
    // Find the index of the current phase
    const currentPhaseIndex = filteredDurations.findIndex(
      d => d.name === selectedPhase
    );
  
    if (currentPhaseIndex === -1) return 'Phase not found';
  
    let cumulativeLow = 0;
    let cumulativeHigh = 0;
  
    // Process phases from the selected phase onwards
    const results = filteredDurations
      .slice(currentPhaseIndex)
      .map((phase, index) => {
        const { low: phaseLow, high: phaseHigh } = convertToHours(phase.value);
  
        // Update cumulative values
        cumulativeLow += phaseLow;
        cumulativeHigh += phaseHigh;
  
        // Adjust for the elapsed time
        const adjustedLow = cumulativeLow - timeInHours;
        const adjustedHigh = cumulativeHigh - timeInHours;
  
        // Calculate phase end times
        const phaseEndTimeLow = new Date(currentTime.getTime() + adjustedLow * 3600 * 1000);
        const phaseEndTimeHigh = new Date(currentTime.getTime() + adjustedHigh * 3600 * 1000);
  
        // Check if phase end time is already past
        if (phaseEndTimeHigh < now) return null;
  
        // Format end times
        const formattedEndTimeLow = phaseEndTimeLow.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const formattedEndTimeHigh = phaseEndTimeHigh.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  
        // Create display text without "(next day)"
        const displayText = index === 0
          ? (
            <strong>
              {phase.name} until {formattedEndTimeLow} - {formattedEndTimeHigh}
            </strong>
          ) : (
            `${phase.name} until ${formattedEndTimeLow} - ${formattedEndTimeHigh}`
          );
  
        return {
          name: phase.name,
          remainingLow: cumulativeLow.toFixed(2),
          remainingHigh: cumulativeHigh.toFixed(2),
          displayText,
          isFirst: index === 0
        };
      })
      .filter(result => result !== null);
  
    // If no results, experience is considered over
    if (results.length === 0) return 'The experience should be over by now.';
  
    const firstVisibleResult = results[0];
  
    // Function to toggle the visibility of the additional info
    const handleToggleInfo = () => {
      setShowInfo(prev => !prev);
    };
  
  // Display results
  const resultsDisplay = results.map(({ displayText, name }, index) => (
    <div key={name} style={{ fontWeight: name === firstVisibleResult.name ? 'bold' : 'normal', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span>{displayText}</span>
      {/* Wyświetl ikonę tylko dla pierwszego elementu */}
      {index === 0 && (
        <i 
          className="zmdi zmdi-info-outline" 
          style={{ marginLeft: '10px', cursor: 'pointer', padding: '10px' }} 
          onClick={handleToggleInfo}  // Kliknięcie ikony toggle tekstu
        ></i>
      )}
    </div>
  ));
  
    return (
      <div>
        {resultsDisplay}
        {showInfo && (  // Tekst dodatkowy widoczny tylko gdy showInfo === true
          <>
            <br />
            <div style={{ fontSize: '0.8em', marginTop: '10px' }}>
              Onset: when effects become noticeable<br />
              Come up: when effects are increasing<br />
              Peak: point of maximum effect intensity<br />
              Offset: start of decline in effects<br />
              After effects: lingering effects
            </div>
          </>
        )}
      </div>
    );
  };
  
  

  // const filteredDuration = roa?.duration?.filter(item =>
  //   item.name.toLowerCase() !== "total" && item.name.toLowerCase() !== "duration" && item.name.toLowerCase() !== "after effects"
  // ) || [];

  const filteredDuration = roa?.duration?.filter(item =>
    item.name.toLowerCase() !== "after effects"
  ) || [];


  const axisDuration = roa?.duration?.filter(item =>
    item.name.toLowerCase() === "total" || item.name.toLowerCase() === "duration" || item.name.toLowerCase() === "after effects"
  ) || [];

  const BarChartDuration = roa?.duration?.filter(item =>
    item.name.toLowerCase() !== "total" &&
    item.name.toLowerCase() !== "duration" &&
    item.name.toLowerCase() !== "after effects"
  ) || [];


  const BarChartData = BarChartDuration.map(d => {
    const { low, high, mid } = convertToHours(d.value);
    return {
      name: d.name,
      mid,
      low,
      high,
      label: d.value,
      errorrelative: mid - low,
    };
  });

  const chartData = filteredDuration.map(d => {
    const { low, high, mid } = convertToHours(d.value);
    return {
      name: d.name,
      mid,
      low,
      high,
      label: d.value,
      errorrelative: mid - low,
    };
  });

  const axisData = axisDuration.map(d => {
    const { low, high } = convertToHours(d.value);
    return {
      name: d.name,
      low,
      high,
      label: abbreviateTimeUnits(d.value),
    };
  });

  
  const calculateTotalValues = () => {
    const hasOffset = filteredDuration.some(d => d.name.toLowerCase() === 'offset');
    const hasTotal = filteredDuration.some(d => d.name.toLowerCase() === 'total');
    const hasDuration = filteredDuration.some(d => d.name.toLowerCase() === 'duration');
  
    if (hasOffset) {
      // Calculate total values (low, mid, high) excluding "after effects", "duration", and "total"
      const totalValues = filteredDuration
        .filter(d => d.name.toLowerCase() !== 'after effects' && d.name.toLowerCase() !== 'duration' && d.name.toLowerCase() !== 'total')
        .reduce((acc, d) => {
          const { low, mid, high } = convertToHours(d.value);
          acc.low += low;
          acc.mid += mid;
          acc.high += high;
          return acc;
        }, { low: 0, mid: 0, high: 0 });
      return totalValues;
    } else if (hasTotal) {
      // Use the value of "total" if it exists
      const total = roa.duration.find(d => d.name.toLowerCase() === 'total');
      if (total) {
        const { low, mid, high } = convertToHours(total.value);
        return { low, mid, high };
      }
    } else if (hasDuration) {
      // Use the value of "duration" if "total" doesn't exist
      const duration = roa.duration.find(d => d.name.toLowerCase() === 'duration');
      if (duration) {
        const { low, mid, high } = convertToHours(duration.value);
        return { low, mid, high };
      }
    }
    return { low: 0, mid: 0, high: 0 }; // Default values if no condition is met
  };
  
  const totalValues = calculateTotalValues();
  const totalLowValue = totalValues.low;
  const totalMidValue = totalValues.mid;
  const totalHighValue = totalValues.high;
  
  const handleTimeChange = (event) => {
    const timeValue = event.target.value;
    
    if (timeValue) {
      // Jeśli wartość czasu jest ustawiona w pickerze, oblicz na podstawie tego
      const [hours, minutes] = timeValue.split(':').map(Number);
      const now = new Date();
      
      // Ustawienie wybranej godziny na dzisiaj
      const selectedDateTime = new Date(now);
      selectedDateTime.setHours(hours);
      selectedDateTime.setMinutes(minutes);
      selectedDateTime.setSeconds(0);
  
      // Jeśli wybrana godzina jest wcześniejsza niż aktualna, traktujemy ją jako czas następnego dnia
      if (selectedDateTime < now) {
        selectedDateTime.setDate(selectedDateTime.getDate() + 1);
      }
  
      const timeDifferenceInMinutes = Math.round((selectedDateTime - now) / 60000); // różnica w minutach
  
      // Ustawienie wybranego czasu w minutach
      setSelectedTime(timeDifferenceInMinutes > 0 ? timeDifferenceInMinutes : 0); // zapewnij, że nie jest ujemny
    } else {
      setSelectedTime(''); // Wyczyść wybrany czas, jeśli input jest pusty
    }
  };
  


  const nameColorMap = {
    'Onset': '#ff69b4',
    'Come up': '#ff7dee',
    'Peak': '#be03fc',
    'Offset': '#ff7dee',
  };
  
  const yValues = {
    onset: 30,
    comeUp: 80,
    peak1: 100,
    peak2: 80,
    offset: 0
  };

  const getLineChartData = (filteredDuration, totalHighValue, yValues, selectedTime) => {
    // Determine the start index based on the selected filter
    const selectedPhaseIndex = filteredDuration.findIndex(d => d.name.toLowerCase() === selectedPhase.toLowerCase());
    if (selectedPhaseIndex === -1) return { lineChartDataLow: [], lineChartDataMid: [], lineChartDataHigh: [] };
  
    const filteredData = filteredDuration.slice(selectedPhaseIndex);
  
    const hasOnset = filteredDuration.some(d => d.name.toLowerCase() === 'onset');
    const hasPeak = filteredDuration.some(d => d.name.toLowerCase() === 'peak');
    const hasOffset = filteredDuration.some(d => d.name.toLowerCase() === 'offset');
    const hasDuration = filteredDuration.some(d => d.name.toLowerCase() === 'duration');
    const hasTotal = filteredDuration.some(d => d.name.toLowerCase() === 'total');
    const hasAfterEffects = filteredDuration.some(d => d.name.toLowerCase() === 'after effects');
    const hasComeUp = filteredDuration.some(d => d.name.toLowerCase() === 'come up');

    // Initialize data arrays
    const cumulativeValues = { low: 0, mid: 0, high: 0 };
    const lineChartDataLow = [];
    const lineChartDataMid = [];
    const lineChartDataHigh = [];
  
    filteredData.forEach((durationData, index) => {
      const { low, mid, high } = convertToHours(durationData.value);

        cumulativeValues.low += low;
        cumulativeValues.mid += mid;
        cumulativeValues.high += high;

        const halfValueLow = cumulativeValues.low - (low / 2);
        const halfValueMid = cumulativeValues.mid - (mid / 2);
        const halfValueHigh = cumulativeValues.high - (high / 2);

      // (hasOnset && hasComeUp && hasPeak && hasOffset

      if (selectedPhaseIndex === 0 && (hasOnset && hasComeUp && hasPeak && hasOffset)) { // Onset filtered
        if (index === 0) {
          lineChartDataLow.push({ time: 0, y: 0 });
          lineChartDataMid.push({ time: 0, y: 0 });
          lineChartDataHigh.push({ time: 0, y: 0 });
          lineChartDataLow.push({ time: cumulativeValues.low, y: yValues.onset }); // do 30%
          lineChartDataMid.push({ time: cumulativeValues.mid, y: yValues.onset }); // do 30%
          lineChartDataHigh.push({ time: cumulativeValues.high, y: yValues.onset }); // do 30%
        }
        if (index === 1) {
          lineChartDataLow.push({ time: cumulativeValues.low, y: yValues.comeUp });
          lineChartDataMid.push({ time: cumulativeValues.mid, y: yValues.comeUp });
          lineChartDataHigh.push({ time: cumulativeValues.high, y: yValues.comeUp });
        }
        if (index === 2) {
          lineChartDataLow.push({ time: halfValueLow, y: yValues.peak1 });
          lineChartDataLow.push({ time: cumulativeValues.low, y: yValues.peak2 });
          lineChartDataMid.push({ time: halfValueMid, y: yValues.peak1 });
          lineChartDataMid.push({ time: cumulativeValues.mid, y: yValues.peak2 });
          lineChartDataHigh.push({ time: halfValueHigh, y: yValues.peak1 });
          lineChartDataHigh.push({ time: cumulativeValues.high, y: yValues.peak2 });
        }
        if (index === 3) {
          lineChartDataLow.push({ time: cumulativeValues.low, y: yValues.offset });
          lineChartDataMid.push({ time: cumulativeValues.mid, y: yValues.offset });
          lineChartDataHigh.push({ time: cumulativeValues.high, y: yValues.offset });
        }
      } else if (selectedPhaseIndex === 1 && (hasOnset && hasComeUp && hasPeak && hasOffset)) { // ComeUp filtered
        if (index === 0) {
          lineChartDataLow.push({ time: 0, y: yValues.onset });
          lineChartDataMid.push({ time: 0, y: yValues.onset });
          lineChartDataHigh.push({ time: 0, y: yValues.onset });
          lineChartDataLow.push({ time: cumulativeValues.low, y: yValues.comeUp });
          lineChartDataMid.push({ time: cumulativeValues.mid, y: yValues.comeUp });
          lineChartDataHigh.push({ time: cumulativeValues.high, y: yValues.comeUp });
        }
        if (index === 1) {
          lineChartDataLow.push({ time: halfValueLow, y: yValues.peak1 });
          lineChartDataLow.push({ time: cumulativeValues.low, y: yValues.peak2 });
          lineChartDataMid.push({ time: halfValueMid, y: yValues.peak1 });
          lineChartDataMid.push({ time: cumulativeValues.mid, y: yValues.peak2 });
          lineChartDataHigh.push({ time: halfValueHigh, y: yValues.peak1 });
          lineChartDataHigh.push({ time: cumulativeValues.high, y: yValues.peak2 });
        }
        if (index === 2) {
          lineChartDataLow.push({ time: cumulativeValues.low, y: yValues.offset });
          lineChartDataMid.push({ time: cumulativeValues.mid, y: yValues.offset });
          lineChartDataHigh.push({ time: cumulativeValues.high, y: yValues.offset });
        }
      } else if (selectedPhaseIndex === 2 && (hasOnset && hasComeUp && hasPeak && hasOffset)) { // Peak filtered
        if (index === 0) {
          lineChartDataLow.push({ time: 0, y: yValues.comeUp });
          lineChartDataMid.push({ time: 0, y: yValues.comeUp });
          lineChartDataHigh.push({ time: 0, y: yValues.comeUp });
          lineChartDataLow.push({ time: halfValueLow, y: yValues.peak1 });
          lineChartDataLow.push({ time: cumulativeValues.low, y: yValues.peak2 });
          lineChartDataMid.push({ time: halfValueMid, y: yValues.peak1 });
          lineChartDataMid.push({ time: cumulativeValues.mid, y: yValues.peak2 });
          lineChartDataHigh.push({ time: halfValueHigh, y: yValues.peak1 });
          lineChartDataHigh.push({ time: cumulativeValues.high, y: yValues.peak2 });
        }
        if (index === 1) {
          lineChartDataLow.push({ time: cumulativeValues.low, y: yValues.offset });
          lineChartDataMid.push({ time: cumulativeValues.mid, y: yValues.offset });
          lineChartDataHigh.push({ time: cumulativeValues.high, y: yValues.offset });
        }
      } else if (selectedPhaseIndex === 3 && (hasOnset && hasComeUp && hasPeak && hasOffset)) { // Offset filtered
        if (index === 0) {
          lineChartDataLow.push({ time: 0, y: yValues.peak2 });
          lineChartDataMid.push({ time: 0, y: yValues.peak2 });
          lineChartDataHigh.push({ time: 0, y: yValues.peak2 });
          lineChartDataLow.push({ time: cumulativeValues.low, y: yValues.offset });
          lineChartDataMid.push({ time: cumulativeValues.mid, y: yValues.offset });
          lineChartDataHigh.push({ time: cumulativeValues.high, y: yValues.offset });
        }}

        ///////////////////

        else if (selectedPhaseIndex === 0 && (hasOnset && hasPeak && hasOffset)) {
          if (index === 0) {
            lineChartDataLow.push({ time: 0, y: 0 });
            lineChartDataMid.push({ time: 0, y: 0 });
            lineChartDataHigh.push({ time: 0, y: 0 });
            lineChartDataLow.push({ time: cumulativeValues.low, y: yValues.onset });
            lineChartDataMid.push({ time: cumulativeValues.mid, y: yValues.onset });
            lineChartDataHigh.push({ time: cumulativeValues.high, y: yValues.onset });
          }
          if (index === 1) {
              lineChartDataLow.push({ time: halfValueLow, y: yValues.peak1 }); // do 100%
              lineChartDataLow.push({ time: cumulativeValues.low, y: yValues.peak2 }); // do 80%
              lineChartDataMid.push({ time: halfValueMid, y: yValues.peak1 }); // do 100%
              lineChartDataMid.push({ time: cumulativeValues.mid, y: yValues.peak2 }); // do 80%
              lineChartDataHigh.push({ time: halfValueHigh, y: yValues.peak1 }); // do 100%
              lineChartDataHigh.push({ time: cumulativeValues.high, y: yValues.peak2 }); // do 80%
          }
          if (index === 2) {
            lineChartDataLow.push({ time: cumulativeValues.low, y: yValues.offset }); // do 0%
            lineChartDataMid.push({ time: cumulativeValues.mid, y: yValues.offset }); // do 0%
            lineChartDataHigh.push({ time: cumulativeValues.high, y: yValues.offset }); // do 0%
        }}

        else if (selectedPhaseIndex === 1 && (hasOnset && hasPeak && hasOffset)) {
          if (index === 0) {
            lineChartDataLow.push({ time: 0, y: yValues.comeUp });
            lineChartDataMid.push({ time: 0, y: yValues.comeUp });
            lineChartDataHigh.push({ time: 0, y: yValues.comeUp });
            lineChartDataLow.push({ time: halfValueLow, y: yValues.peak1 });
            lineChartDataLow.push({ time: cumulativeValues.low, y: yValues.peak2 });
            lineChartDataMid.push({ time: halfValueMid, y: yValues.peak1 });
            lineChartDataMid.push({ time: cumulativeValues.mid, y: yValues.peak2 });
            lineChartDataHigh.push({ time: halfValueHigh, y: yValues.peak1 });
            lineChartDataHigh.push({ time: cumulativeValues.high, y: yValues.peak2 });
          }
          if (index === 1) {
            lineChartDataLow.push({ time: cumulativeValues.low, y: yValues.offset }); // do 0%
            lineChartDataMid.push({ time: cumulativeValues.mid, y: yValues.offset }); // do 0%
            lineChartDataHigh.push({ time: cumulativeValues.high, y: yValues.offset }); // do 0%
        }}

        else if (selectedPhaseIndex === 2 && (hasOnset && hasPeak && hasOffset)) {
          if (index === 0) {
            lineChartDataLow.push({ time: 0, y: yValues.peak2 });
            lineChartDataMid.push({ time: 0, y: yValues.peak2 });
            lineChartDataHigh.push({ time: 0, y: yValues.peak2 });
            lineChartDataLow.push({ time: cumulativeValues.low, y: yValues.offset });
            lineChartDataMid.push({ time: cumulativeValues.mid, y: yValues.offset });
            lineChartDataHigh.push({ time: cumulativeValues.high, y: yValues.offset });
        }}

        //////////////////////


            else if (selectedPhaseIndex === 0 && (hasOnset && (hasPeak || hasComeUp) && (hasTotal || hasDuration || hasOffset))) {
            if (index === 0) {
              lineChartDataLow.push({ time: 0, y: 0 });
              lineChartDataMid.push({ time: 0, y: 0 });
              lineChartDataHigh.push({ time: 0, y: 0 });  
              lineChartDataLow.push({ time: cumulativeValues.low, y: yValues.onset });
                lineChartDataMid.push({ time: cumulativeValues.mid, y: yValues.onset });
                lineChartDataHigh.push({ time: cumulativeValues.high, y: yValues.onset });
            }
            if (index === 1) {
              lineChartDataLow.push({ time: halfValueLow, y: yValues.peak1 }); // do 100%
                lineChartDataLow.push({ time: cumulativeValues.low, y: yValues.peak2 }); // do 80%
                lineChartDataMid.push({ time: halfValueMid, y: yValues.peak1 }); // do 100%
                lineChartDataMid.push({ time: cumulativeValues.mid, y: yValues.peak2 }); // do 80%
                lineChartDataHigh.push({ time: halfValueHigh, y: yValues.peak1 }); // do 100%
                lineChartDataHigh.push({ time: cumulativeValues.high, y: yValues.peak2 }); // do 80%
            }
            if (index === 2) {
                  lineChartDataLow.push({ time: totalLowValue, y: 0 });
                  lineChartDataMid.push({ time: totalMidValue, y: 0 });
                  lineChartDataHigh.push({ time: totalHighValue, y: 0 });
                }}
  
                else if (selectedPhaseIndex === 1 && (hasOnset && (hasPeak || hasComeUp) && (hasTotal || hasDuration || hasOffset))) {
                  if (index === 0) {
                    lineChartDataLow.push({ time: 0, y: yValues.comeUp });
                    lineChartDataMid.push({ time: 0, y: yValues.comeUp });
                    lineChartDataHigh.push({ time: 0, y: yValues.comeUp });  
                    lineChartDataLow.push({ time: halfValueLow, y: yValues.peak1 }); // do 100%
                      lineChartDataLow.push({ time: cumulativeValues.low, y: yValues.peak2 }); // do 80%
                      lineChartDataMid.push({ time: halfValueMid, y: yValues.peak1 }); // do 100%
                      lineChartDataMid.push({ time: cumulativeValues.mid, y: yValues.peak2 }); // do 80%
                      lineChartDataHigh.push({ time: halfValueHigh, y: yValues.peak1 }); // do 100%
                      lineChartDataHigh.push({ time: cumulativeValues.high, y: yValues.peak2 }); // do 80%
                  }
                  if (index === 1) {
                        lineChartDataLow.push({ time: totalLowValue, y: 0 });
                        lineChartDataMid.push({ time: totalMidValue, y: 0 });
                        lineChartDataHigh.push({ time: totalHighValue, y: 0 });
                      }}

                      else if (selectedPhaseIndex === 2 && (hasOnset && (hasPeak || hasComeUp) && (hasTotal || hasDuration || hasOffset))) {
                        if (index === 0) {
                          lineChartDataLow.push({ time: 0, y: yValues.peak2 });
                          lineChartDataMid.push({ time: 0, y: yValues.peak2 });
                          lineChartDataHigh.push({ time: 0, y: yValues.peak2 });    
                          lineChartDataLow.push({ time: totalLowValue, y: 0 });
                              lineChartDataMid.push({ time: totalMidValue, y: 0 });
                              lineChartDataHigh.push({ time: totalHighValue, y: 0 });
                            }}

              ////////////////////////////////

               else if (selectedPhaseIndex === 0 && (hasOnset && hasPeak && (hasTotal || hasDuration || hasOffset))) {
                if (index === 0) {
                  lineChartDataLow.push({ time: 0, y: 0 });
                  lineChartDataMid.push({ time: 0, y: 0 });
                  lineChartDataHigh.push({ time: 0, y: 0 });  
                  lineChartDataLow.push({ time: cumulativeValues.low, y: yValues.onset });
                    lineChartDataMid.push({ time: cumulativeValues.mid, y: yValues.onset });
                    lineChartDataHigh.push({ time: cumulativeValues.high, y: yValues.onset });
                }
                if (index === 1) {
                    lineChartDataLow.push({ time: halfValueLow, y: yValues.peak1 }); // do 100%
                    lineChartDataLow.push({ time: cumulativeValues.low, y: yValues.peak2 }); // do 80%
                    lineChartDataMid.push({ time: halfValueMid, y: yValues.peak1 }); // do 100%
                    lineChartDataMid.push({ time: cumulativeValues.mid, y: yValues.peak2 }); // do 80%
                    lineChartDataHigh.push({ time: halfValueHigh, y: yValues.peak1 }); // do 100%
                    lineChartDataHigh.push({ time: cumulativeValues.high, y: yValues.peak2 }); // do 80%
                }
                if (index === 2) {
                      lineChartDataLow.push({ time: totalLowValue, y: 0 });
                      lineChartDataMid.push({ time: totalMidValue, y: 0 });
                      lineChartDataHigh.push({ time: totalHighValue, y: 0 });
                    }}

               else if (selectedPhaseIndex === 1 && (hasOnset && hasPeak && (hasTotal || hasDuration || hasOffset))) {
                if (index === 0) {
                  lineChartDataLow.push({ time: 0, y: yValues.onset });
                  lineChartDataMid.push({ time: 0, y: yValues.onset });
                  lineChartDataHigh.push({ time: 0, y: yValues.onset });    
                  lineChartDataLow.push({ time: halfValueLow, y: yValues.peak1 }); // do 100%
                    lineChartDataLow.push({ time: cumulativeValues.low, y: yValues.peak2 }); // do 80%
                    lineChartDataMid.push({ time: halfValueMid, y: yValues.peak1 }); // do 100%
                    lineChartDataMid.push({ time: cumulativeValues.mid, y: yValues.peak2 }); // do 80%
                    lineChartDataHigh.push({ time: halfValueHigh, y: yValues.peak1 }); // do 100%
                    lineChartDataHigh.push({ time: cumulativeValues.high, y: yValues.peak2 }); // do 80%
                }
                if (index === 1) {
                      lineChartDataLow.push({ time: totalLowValue, y: 0 });
                      lineChartDataMid.push({ time: totalMidValue, y: 0 });
                      lineChartDataHigh.push({ time: totalHighValue, y: 0 });
                    }}

               else if (selectedPhaseIndex === 2 && (hasOnset && hasPeak && (hasTotal || hasDuration || hasOffset))) {
                if (index === 0) {
                  lineChartDataLow.push({ time: 0, y: yValues.peak2 });
                  lineChartDataMid.push({ time: 0, y: yValues.peak2 });
                  lineChartDataHigh.push({ time: 0, y: yValues.peak2 });      
                  lineChartDataLow.push({ time: totalLowValue, y: 0 });
                      lineChartDataMid.push({ time: totalMidValue, y: 0 });
                      lineChartDataHigh.push({ time: totalHighValue, y: 0 });
                    }}

              ////////////////////////////////

                   else if (selectedPhaseIndex === 0 && (hasComeUp && (hasPeak || hasOffset) && (hasTotal || hasDuration ))) {
                    if (index === 0) {
                      lineChartDataLow.push({ time: 0, y: 0 });
                      lineChartDataMid.push({ time: 0, y: 0 });
                      lineChartDataHigh.push({ time: 0, y: 0 });  
                      lineChartDataLow.push({ time: cumulativeValues.low, y: yValues.peak1 }); // do 100%
                      lineChartDataMid.push({ time: cumulativeValues.mid, y: yValues.peak1 }); // do 100%
                      lineChartDataHigh.push({ time: cumulativeValues.high, y: yValues.peak1 }); // do 100%
                    }
                    if (index === 1) {
                      lineChartDataLow.push({ time: cumulativeValues.low, y: yValues.offset });
                      lineChartDataMid.push({ time: cumulativeValues.mid, y: yValues.offset });
                      lineChartDataHigh.push({ time: cumulativeValues.high, y: yValues.offset });
                    }}

                        else if (selectedPhaseIndex === 1 && (hasComeUp && (hasPeak || hasOffset) && (hasTotal || hasDuration ))) {
                          if (index === 0) {
                              lineChartDataLow.push({ time: 0, y: yValues.peak2 }); // do 80%
                              lineChartDataMid.push({ time: 0, y: yValues.peak2 }); // do 80%
                              lineChartDataHigh.push({ time: 0, y: yValues.peak2 }); // do 80%
                          }
                          if (index === 1) {
                                lineChartDataLow.push({ time: totalLowValue, y: 0 });
                                lineChartDataMid.push({ time: totalMidValue, y: 0 });
                                lineChartDataHigh.push({ time: totalHighValue, y: 0 });
                              }}



                                                  ////////////////////////////////

                   else if (selectedPhaseIndex === 0 && (hasComeUp && hasPeak && hasOffset)) {
                    if (index === 0) {
                      lineChartDataLow.push({ time: 0, y: 0 });
                      lineChartDataMid.push({ time: 0, y: 0 });
                      lineChartDataHigh.push({ time: 0, y: 0 });  
                      lineChartDataLow.push({ time: cumulativeValues.low, y: yValues.peak1 });
                        lineChartDataMid.push({ time: cumulativeValues.mid, y: yValues.peak1 });
                        lineChartDataHigh.push({ time: cumulativeValues.high, y: yValues.peak1 });
                    }
                    if (index === 1) {
                          lineChartDataLow.push({ time: totalLowValue, y: 0 });
                          lineChartDataMid.push({ time: totalMidValue, y: 0 });
                          lineChartDataHigh.push({ time: totalHighValue, y: 0 });
                        }}

                        else if (selectedPhaseIndex === 1 && (hasComeUp && hasPeak && hasOffset)) {
                          if (index === 0) {
                            lineChartDataLow.push({ time: 0, y: yValues.peak1 });
                            lineChartDataMid.push({ time: 0, y: yValues.peak1 });
                            lineChartDataHigh.push({ time: 0, y: yValues.peak1 });    
                          }
                          if (index === 1) {
                                lineChartDataLow.push({ time: totalLowValue, y: 0 });
                                lineChartDataMid.push({ time: totalMidValue, y: 0 });
                                lineChartDataHigh.push({ time: totalHighValue, y: 0 });
                              }
                                    }

              ////////////////////////////////

              else if (selectedPhaseIndex === 0 && ((hasOnset || hasComeUp || hasPeak) && (hasOffset || hasDuration || hasTotal))) {
                if (index === 0) {
                  lineChartDataLow.push({ time: 0, y: 0 });
                  lineChartDataMid.push({ time: 0, y: 0 });
                  lineChartDataHigh.push({ time: 0, y: 0 });    
                  lineChartDataLow.push({ time: cumulativeValues.low, y: yValues.peak1 }); // do 80%
                  lineChartDataMid.push({ time: cumulativeValues.mid, y: yValues.peak1 }); // do 80%
                  lineChartDataHigh.push({ time: cumulativeValues.high, y: yValues.peak1 }); // do 80%
                  //  lineChartDataLow.push({ time: halfValueLow, y: yValues.peak1 }); // do 100%
                  //   lineChartDataMid.push({ time: halfValueMid, y: yValues.peak1 }); // do 100%
                  //   lineChartDataHigh.push({ time: halfValueHigh, y: yValues.peak1 }); // do 100%
                }
                if (index === 1) {

                  lineChartDataLow.push({ time: totalLowValue, y: 0 });
                      lineChartDataMid.push({ time: totalMidValue, y: 0 });
                      lineChartDataHigh.push({ time: totalHighValue, y: 0 });
                }}



                  else if (selectedPhaseIndex === 1 && ((hasOnset || hasComeUp || hasPeak) && (hasOffset || hasDuration || hasTotal))) {
                    if (index === 0) {
                      lineChartDataLow.push({ time: 0, y: yValues.peak1 });
                      lineChartDataMid.push({ time: 0, y: yValues.peak1 });
                      lineChartDataHigh.push({ time: 0, y: yValues.peak1 }); 
                      lineChartDataLow.push({ time: totalLowValue, y: 0 });
                      lineChartDataMid.push({ time: totalMidValue, y: 0 });
                      lineChartDataHigh.push({ time: totalHighValue, y: 0 });
                    }
                  }


                      /////////////////////////////

                    
         else if ((selectedPhaseIndex === 0 && (hasPeak || hasOffset) && (hasDuration || hasTotal))) {
          if (index === 0) {
            lineChartDataLow.push({ time: 0, y: 0 });
            lineChartDataMid.push({ time: 0, y: 0 });
            lineChartDataHigh.push({ time: 0, y: 0 });   
              lineChartDataLow.push({ time: cumulativeValues.low, y: yValues.peak1 });
              lineChartDataMid.push({ time: cumulativeValues.mid, y: yValues.peak1 });
              lineChartDataHigh.push({ time: cumulativeValues.high, y: yValues.peak1 });
                    } 
            if (index === 1) {
              lineChartDataLow.push({ time: totalLowValue, y: 0 });
              lineChartDataMid.push({ time: totalMidValue, y: 0 });
              lineChartDataHigh.push({ time: totalHighValue, y: 0 });
          }
        }



                            
        else if (selectedPhaseIndex === 1 && ((hasPeak || hasOffset) && (hasDuration || hasTotal))) {
            if (index === 0) {
              lineChartDataLow.push({ time: 0, y: yValues.peak1 });
              lineChartDataMid.push({ time: 0, y: yValues.peak1 });
              lineChartDataHigh.push({ time: 0, y: yValues.peak1 }); 
              lineChartDataLow.push({ time: totalLowValue, y: 0 });
              lineChartDataMid.push({ time: totalMidValue, y: 0 });
              lineChartDataHigh.push({ time: totalHighValue, y: 0 });
          }
        }
        
      });

//////////////
/// IF SUBSTANCE ROA HAS ONLY 1 DURATION VALUE, or only onset + peak, DISABLE LINE CHART and calculator
// if substance has only "total or duration", disable also bar chart

    return { lineChartDataLow, lineChartDataMid, lineChartDataHigh };
  };
  
  // Example usage of the function
  const { lineChartDataLow, lineChartDataMid, lineChartDataHigh } = getLineChartData(filteredDuration, totalHighValue, yValues, 'peak');
  

const hasMultipleDurations = roa?.duration?.length > 1; // Check if there's more than 1 duration value

useEffect(() => {
  if (roa?.duration && selectedPhase === "") {
    setSelectedPhase(roa.duration[0].name); // Ustawienie domyślnej wartości na pierwszy element
  }
}, [roa, selectedPhase]);


return (
  <div style={{ marginTop: '30px', marginBottom: '30px' }}>
    {visible && (
      <div>
        {/* Nagłówek z ikoną */}
        <div style={{ paddingTop: 20, display: 'flex', justifyContent: 'center', alignItems: 'baseline', marginBottom: '30px' }}>
          <h2 style={{ margin: 0 }}>Duration</h2>
          <i 
            className="zmdi zmdi-info-outline" 
            style={{ marginLeft: '10px', cursor: 'pointer', padding: '10px' }} 
            onClick={handleInfoClick}  // Tutaj używasz funkcji bez this
          ></i>
        </div>


        {/* Duration Axis */}
{/* Duration Axis */}
<div> 
  {axisData.length > 0 && (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {axisData.map((duration, index) => {
          const [start, end] = duration.label.split(' - ');
          
          // Funkcja sprawdzająca, czy etykieta zawiera liczby
          const isLabelNumeric = !isNaN(parseFloat(start));
          
          // Funkcja formatująca etykietę
          const formatLabel = (label) => {
            const value = parseFloat(label);
            return isNaN(value) ? capitalizeFirstLetter(label) : (value % 1 === 0 ? value.toString() : value.toFixed(1));
          };

          // Funkcja zmieniająca pierwszą literę na wielką
          const capitalizeFirstLetter = (string) => {
            return string.charAt(0).toUpperCase() + string.slice(1);
          };

          return (
            <span
              key={index}
              style={{ flex: 1, textAlign: 'center', color: '#333', fontSize: '0.8em', cursor: 'pointer' }}
            >
              {isLabelNumeric 
                ? `${formatLabel(start)} - ${formatLabel(end)} ${duration.label.replace(/[0-9. -]+/, '')}` 
                : capitalizeFirstLetter(duration.label)
              }
            </span>
          );
        })}
      </div>
      <div
        style={{
          boxShadow: 'rgba(0, 0, 0, 0.4) 0px 2px 4px, rgba(0, 0, 0, 0.3) 0px 7px 13px -15px, rgba(0, 0, 0, 0.2) 0px -4px 0px inset',
          display: 'flex',
          height: '15px',
          margin: '5px 0px 8px 0px',
          borderRadius: '2px',
          overflow: 'hidden',
          backgroundImage: `linear-gradient(to right, #ff69b4 0%, #ff7dee 10%, #be03fc 25%, #7703fc 50%, darkblue 100%)`,
          width: '100%',
        }} 
      />
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {axisData.map((duration, index) => (
          <span key={index} style={{ flex: 1, textAlign: 'center', color: '#333', fontSize: '0.8em', marginBottom: '25px' }}>
            {duration.name}
          </span>
        ))}
      </div>
    </>
  )}
</div>


        {/* Conditionally render the BarChart */}
        <div style={{ marginLeft: '-10px', height: 'auto', marginRight: '-45px', fontSize: '0.8em' }}>
          {hasMultipleDurations && BarChartData.length > 1 && (
            <ResponsiveContainer width="100%" height={300} marginTop="30px" marginBottom="30px">
              <BarChart
                data={BarChartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis
                  tickFormatter={(value) => {
                    if (unit === 'minutes') {
                      return `${Math.round(value * 60)}`;
                    } else if (unit === 'hours') {
                      return `${Math.round(value)}`;
                    } else if (unit === 'seconds') {
                      return `${Math.round(value * 3600)}`;
                    }
                    return Math.round(value);
                  }}
                  label={{ value: `Time (${unit})`, angle: 90, position: "insideCenter", dx: 7 }}
                  orientation="right"
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="mid" fill="#8884d8">
                  {BarChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={nameColorMap[entry.name] || '#8884d8'} />
                  ))}
                  {BarChartData.map((d, index) => (
                    <ErrorBar
                      key={`errorbar-${index}`}
                      dataKey="errorrelative"
                      width={2}
                      strokeWidth={2}
                      direction="y"
                      stroke="#000000"
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Line Chart for Effects */}
        <div style={{ marginTop: '10px', textAlign: 'center', paddingTop: '30px', marginLeft: '-10px', marginRight: '-45px', fontSize: '0.8em' }}>
          {hasMultipleDurations && chartData.length > 0 && (
            <ResponsiveContainer width="100%" height={300} margin={{ top: 30, right: 30, left: 20, bottom: 30 }} padding={{ left: 10, top: 30 }}>
              <LineChart
                data={chartData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  dataKey="time"
                  domain={[0, totalHighValue]}
                  allowDuplicatedCategory={false}
                  tickCount={10}
                  tickFormatter={(time) => {
                    const selectedTimeValue = parseFloat(selectedTime) || 0;
                    const selectedTimeInMillis = selectedTimeValue * 60000;
                    const currentTimeInMillis = new Date().getTime();
                    const adjustedTimeInMillis = currentTimeInMillis + (time * 60 * 60 * 1000 + selectedTimeInMillis);
                    return new Date(adjustedTimeInMillis).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    });
                  }}
                />
                <YAxis
                  type="number"
                  dataKey="y"
                  ticks={[0, 10, 50, 100]}
                  tickFormatter={(value) => (value === 100 ? 'Peak' : '')}
                  domain={[0, 100]}
                  orientation="right"
                  label={{
                    value: 'Effect',
                    angle: 90,
                    position: 'insideCenter',
                    dx: 4,
                    textAnchor: 'middle',
                  }}
                />
                <Tooltip content={<LinearTooltip />} />
                <defs>
                  <linearGradient id="colorGradient" x1="0" y1="1" x2="0" y2="0">
                    <stop offset="0%" stopColor="#ff69b4" stopOpacity={0.6} />
                    <stop offset="25%" stopColor="#ff7dee" stopOpacity={0.6} />
                    <stop offset="80%" stopColor="#be03fc" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#7703fc" stopOpacity={0.6} />
                  </linearGradient>
                </defs>
                <Line
                  type="monotone"
                  dataKey="y"
                  data={lineChartDataLow}
                  stroke="url(#colorGradient)"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ r: 6 }}
                  name="Short"
                />
                <Line
                  type="monotone"
                  dataKey="y"
                  data={lineChartDataMid}
                  stroke="url(#colorGradient)"
                  strokeWidth={5}
                  dot={{ r: 6 }}
                  name="Average"
                />
                <Line
                  type="monotone"
                  dataKey="y"
                  data={lineChartDataHigh}
                  stroke="url(#colorGradient)"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ r: 6 }}
                  name="Long"
                />
                <Legend
                  verticalAlign="center"
                  wrapperStyle={{
                    position: 'relative',
                    transform: 'translateY(-50px)',
                    left: '-10px',
                    marginTop: '25px',
                    paddingBottom: '0px'
                  }}
                  iconSize={0}
                  formatter={(value, entry) => {
                    const dashStyle = entry.value.includes('Short') || entry.value.includes('Long') ? 'Dashed' : 'Solid';
                    return (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div
                          style={{
                            width: 20,
                            borderBottom: `2px ${dashStyle.toLowerCase()}`,
                            marginRight: 8,
                          }}
                        />
                        <span>{value}</span>
                      </div>
                    );
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Select Phase */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingTop: '20px'}}>
          <select
            value={selectedPhase}
            onChange={e => setSelectedPhase(e.target.value)}
            style={{ flex: 1, width: 'auto', marginTop: '30px' }}
          >
            <option value="" disabled>Effect</option>
            {roa?.duration
              ?.filter(phase => !/after effects|duration|total/i.test(phase.name))
              .map(phase => (
                <option key={phase.name} value={phase.name}>
                  {phase.name}
                </option>
              ))}
          </select>
          <div style={{ marginTop: '30px' }}> started at </div>
          <div style={{ marginTop: '30px' }}>
            <input
              aria-label="Time"
              type="time"
              onChange={handleTimeChange}
              style={{ padding: '8px', fontSize: '16px', width: 'auto', appearance: 'auto', marginBottom: '10px', height: '40px' }}
            />
          </div>
        </div>

        {/* Remaining Time Display */}
        <div style={{ marginTop: '20px' }}>
          {calculateRemainingTime()}
        </div>
      </div>
    )}
  </div>
);
}